import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCurrentUser } from '../../app/AuthProvider.tsx';
import { api } from '../../lib/api/client.js';
import type { AdminAlertsResponse } from '../../lib/api/types.js';
import { Button } from '../../ui/primitives/Button.tsx';
import { Icon } from '../../ui/primitives/icon/index.ts';
import { Surface } from '../../ui/primitives/Surface.tsx';
import { Text } from '../../ui/primitives/Text.tsx';
import { ADMIN_ALERTS_CHANGED } from './admin-security.ts';
import styles from './admin.module.css';

/**
 * THE BANNER ABOVE EVERY ADMIN SCREEN when an administrator is frozen
 * (FR-084) — rendered by the admin route itself, so no screen can forget it.
 *
 * It says two different things depending on who is reading:
 *
 *  - the frozen administrator is told their changes are stopped, why, and
 *    that someone else lifts it — so a save that fails reads as a decision,
 *    not as something broken;
 *  - everybody else is told who is frozen and why, with the way to Usuários
 *    and the Atividade log, because a freeze is exactly the moment to look at
 *    what that account has been doing.
 *
 * Nothing at all when nobody is frozen, which is almost always. It re-reads
 * on every admin navigation and whenever a write reports a change
 * (`ADMIN_ALERTS_CHANGED`); a failed read leaves it as it was rather than
 * putting an error above a screen that is otherwise working.
 */
export function AdminAlerts() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const me = useCurrentUser();
  const [alerts, setAlerts] = useState<AdminAlertsResponse | null>(null);

  const load = useCallback(() => {
    api
      .adminAlerts()
      .then(setAlerts)
      .catch(() => undefined);
  }, []);

  useEffect(load, [load, pathname]);

  useEffect(() => {
    window.addEventListener(ADMIN_ALERTS_CHANGED, load);
    return () => window.removeEventListener(ADMIN_ALERTS_CHANGED, load);
  }, [load]);

  if (!alerts || alerts.frozenAdmins.length === 0) return null;

  const self = alerts.frozenAdmins.find((admin) => admin.id === me?.id);
  const others = alerts.frozenAdmins.filter((admin) => admin.id !== me?.id);

  return (
    <div className={styles.alerts} role="status" data-testid="admin-alerts">
      <Surface layer={2} radius="lg" inset>
        <div className={styles.alert}>
          <span className={styles.alertIcon} aria-hidden="true">
            <Icon name="alert-circle" size={20} />
          </span>
          <div className={styles.heading}>
            {alerts.selfFrozen ? (
              <>
                <Text as="p" variant="label">
                  Suas alterações estão congeladas
                </Text>
                <Text as="p" variant="body-sm" tone="muted">
                  {self?.reason ? `${self.reason} ` : ''}Você ainda vê todas as telas, mas não pode
                  mudar nada até outro administrador liberar em Usuários.
                </Text>
              </>
            ) : (
              <>
                <Text as="p" variant="label">
                  {others.length === 1
                    ? `As alterações de ${others[0]!.name} estão congeladas`
                    : `${others.length} administradores estão congelados`}
                </Text>
                <Text as="p" variant="body-sm" tone="muted">
                  {others.length === 1 && others[0]!.reason ? `${others[0]!.reason} ` : ''}
                  Veja o que foi feito em Atividade antes de liberar em Usuários.
                </Text>
              </>
            )}
          </div>
          {alerts.selfFrozen ? null : (
            <div className={styles.rowActions}>
              <Button variant="ghost" size="compact" onClick={() => navigate('/admin/atividade')}>
                Ver atividade
              </Button>
              <Button
                variant="secondary"
                size="compact"
                onClick={() => navigate('/admin/usuarios')}
              >
                Ir para Usuários
              </Button>
            </div>
          )}
        </div>
      </Surface>
    </div>
  );
}
